import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Invite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const referralToken = searchParams.get('token');
  const legacyReferralCode = searchParams.get('ref'); // Legacy support

  const processInvitationForLoggedInUser = async () => {
    if (!user?.id) return;
    
    setIsProcessing(true);
    try {
      if (referralToken) {
        // Process referral session with new token-based system
        const { data, error } = await supabase.rpc('process_referral_session', {
          session_token_param: referralToken,
          user_id_param: user.id
        });

        if (error) {
          console.error('Error processing referral session:', error);
          toast.error('Invalid or expired invitation link');
        } else {
          const typedData = data as { success?: boolean; error?: string; message?: string };
          if (typedData?.success) {
            toast.success('Welcome! You can now find recommended connections in the search section.');
          } else {
            toast.error('Unable to process invitation: ' + (typedData?.error || 'Unknown error'));
          }
        }
      } else if (legacyReferralCode) {
        // Legacy: Process any pending invitations for this user
        const { data, error } = await supabase.rpc('process_pending_invitations_for_user', {
          user_email: user.email
        });

        if (error) {
          console.error('Error processing pending invitations:', error);
        } else if (data && data.length > 0) {
          const processedCount = data.length;
          toast.success(`Welcome! Found ${processedCount} connection${processedCount > 1 ? 's' : ''} - check the search section for recommendations.`);
        } else {
          toast.success('Welcome! Check the search section for recommended connections.');
        }
      }
    } catch (error) {
      console.error('Error processing invitations:', error);
      toast.error('Unable to process invitation');
    } finally {
      setIsProcessing(false);
      navigate('/friends?tab=search&from=invite', { replace: true });
    }
  };

  useEffect(() => {
    if (loading) return;

    if (!referralToken && !legacyReferralCode) {
      // No referral token or code, redirect appropriately
      toast.error('Invalid invitation link');
      navigate(user ? '/friends' : '/login', { replace: true });
      return;
    }

    if (user) {
      // User is logged in, process any pending invitations
      processInvitationForLoggedInUser();
    } else {
      // User is not logged in, redirect to login with referral preserved
      const redirectParam = referralToken ? `token=${referralToken}` : `ref=${legacyReferralCode}`;
      navigate(`/login?${redirectParam}&from=invite`, { replace: true });
    }
  }, [user, loading, referralToken, legacyReferralCode, navigate]);

  // Show loading while processing
  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-uplivion-blue mx-auto mb-4"></div>
        <p className="text-gray-600">Processing invitation...</p>
      </div>
    </div>
  );
};

export default Invite;